import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { X, ChevronLeft, ChevronRight, Maximize, Eye, EyeOff, Timer, Users } from "lucide-react";
import { useLiveFollow, FollowQrHotspot } from "@/components/FollowQr";
import {
  buildSlides,
  slideFooter,
  slideMetrics,
  formatClock,
  SLIDE_W_PT,
  SLIDE_H_PT,
  DEFAULT_FONT,
  DEFAULT_FONT_PT,
  MIN_TRANSITION_MS,
  MAX_TRANSITION_MS,
  DEFAULT_TRANSITION_MS,
} from "@/lib/slides";

function useStageScale(ref) {
  const [scale, setScale] = useState(1);
  useEffect(() => {
    const measure = () => {
      const el = ref.current;
      if (!el) return;
      setScale(Math.min(el.clientWidth / SLIDE_W_PT, el.clientHeight / SLIDE_H_PT));
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [ref]);
  return scale;
}

export function PresentMode({ session, startIndex = 0, rehearsal = false, canShare = false, onRehearsalEnd, onClose }) {
  const font = session?.slide_font || DEFAULT_FONT;
  const fontSize = session?.slide_font_size || DEFAULT_FONT_PT;
  const metrics = slideMetrics(fontSize);
  const slides = useMemo(() => buildSlides(session?.segments || [], fontSize), [session, fontSize]);
  const [index, setIndex] = useState(startIndex);
  const [visible, setVisible] = useState(true);
  const transitionMs = Math.min(
    MAX_TRANSITION_MS,
    Math.max(MIN_TRANSITION_MS, session?.slide_transition_ms || DEFAULT_TRANSITION_MS)
  );
  const half = Math.round(transitionMs / 2);
  const fadeTimer = useRef(null);
  const [blanked, setBlanked] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const stageRef = useRef(null);
  const scale = useStageScale(stageRef);
  const timings = useRef([]);
  const lastMark = useRef(Date.now());

  const record = useCallback((slideIdx) => {
    const now = Date.now();
    timings.current.push({ slideIndex: slideIdx, seconds: (now - lastMark.current) / 1000 });
    lastMark.current = now;
  }, []);

  // Fade the slide out through black, swap, then fade the new slide in.
  const goTo = useCallback(
    (target) => {
      if (target < 0 || target >= slides.length || target === index) return;
      if (rehearsal && target > index) record(index);
      setVisible(false);
      clearTimeout(fadeTimer.current);
      fadeTimer.current = setTimeout(() => {
        setIndex(target);
        setVisible(true);
      }, half);
    },
    [index, slides.length, rehearsal, record, half]
  );

  const next = useCallback(() => goTo(index + 1), [goTo, index]);
  const prev = useCallback(() => goTo(index - 1), [goTo, index]);

  useEffect(() => () => clearTimeout(fadeTimer.current), []);

  const finish = useCallback(() => {
    if (rehearsal) {
      record(index);
      onRehearsalEnd?.(timings.current, slides);
    }
    onClose();
  }, [rehearsal, record, index, onRehearsalEnd, onClose, slides]);

  useEffect(() => {
    if (!rehearsal) return;
    const started = Date.now();
    const id = setInterval(() => setElapsed((Date.now() - started) / 1000), 1000);
    return () => clearInterval(id);
  }, [rehearsal]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "b" || e.key === "B") {
        e.preventDefault();
        setBlanked((b) => !b);
      } else if (["ArrowRight", "ArrowDown", "PageDown", " ", "Enter"].includes(e.key)) {
        e.preventDefault();
        setBlanked(false);
        next();
      } else if (["ArrowLeft", "ArrowUp", "PageUp"].includes(e.key)) {
        e.preventDefault();
        setBlanked(false);
        prev();
      } else if (e.key === "Escape") {
        if (blanked) setBlanked(false);
        else finish();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev, finish, blanked]);

  const goFullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen();
    else document.documentElement.requestFullscreen?.();
  };

  const { followUrl, followers } = useLiveFollow({
    sessionId: session?.id,
    slides,
    font,
    fontSize,
    index,
    blanked,
    enabled: canShare && !rehearsal,
  });

  const songMap = useMemo(() => {
    const current = slides[index];
    if (!current) return [];
    return slides
      .map((s, i) => ({ s, i }))
      .filter(({ s }) => s.segmentIndex === current.segmentIndex && s.lines[0]?.label)
      .map(({ s, i }) => ({ slideIndex: i, label: s.lines[0].text }));
  }, [slides, index]);

  const activeSongMapSlide = useMemo(() => {
    let active = -1;
    songMap.forEach((p) => { if (p.slideIndex <= index) active = p.slideIndex; });
    return active;
  }, [songMap, index]);

  if (!slides.length) {
    return (
      <div className="fixed inset-0 z-[200] bg-black text-white flex flex-col items-center justify-center gap-4" data-testid="present-mode-overlay">
        <p className="text-xl text-white/70" style={{ fontFamily: font }} data-testid="present-mode-empty">
          Add segments to your session to present it.
        </p>
        <button onClick={onClose} className="text-sm uppercase tracking-widest text-white/60 hover:text-white" data-testid="present-mode-close-button">
          Exit
        </button>
      </div>
    );
  }

  const slide = slides[index];
  const pt = (v) => `${v * scale}px`;

  return (
    <div className="fixed inset-0 z-[200] bg-black text-white select-none" style={{ fontFamily: font }} data-testid="present-mode-overlay">
      <div className="absolute top-4 right-4 flex items-center gap-2 opacity-20 hover:opacity-100 transition-opacity z-10">
        {rehearsal && (
          <span className="flex items-center gap-1.5 font-mono text-xs text-white/70 mr-2" data-testid="rehearsal-timer">
            <Timer className="h-3.5 w-3.5" /> {formatClock(elapsed)}
          </span>
        )}
        {followUrl && followers > 0 && (
          <span className="flex items-center gap-1.5 font-mono text-xs text-white/70 mr-2" title={`${followers} phone${followers > 1 ? "s" : ""} following`} data-testid="present-mode-follower-count">
            <Users className="h-3.5 w-3.5" /> {followers}
          </span>
        )}
        <button
          onClick={(e) => { e.stopPropagation(); setBlanked((b) => !b); }}
          className="p-2 rounded-full hover:bg-white/10"
          title="Blank the screen (B)"
          data-testid="present-mode-blank-button"
        >
          {blanked ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
        </button>
        <button onClick={goFullscreen} className="p-2 rounded-full hover:bg-white/10" title="Toggle fullscreen" data-testid="present-mode-fullscreen-button">
          <Maximize className="h-4 w-4" />
        </button>
        <button onClick={finish} className="p-2 rounded-full hover:bg-white/10" title="Exit presentation (Esc)" data-testid="present-mode-close-button">
          <X className="h-4 w-4" />
        </button>
      </div>

      <div ref={stageRef} className="absolute inset-0 cursor-pointer" onClick={next} data-testid="present-mode-slide">
        <div className="absolute left-1/2 top-1/2" style={{ width: pt(SLIDE_W_PT), height: pt(SLIDE_H_PT), transform: "translate(-50%, -50%)" }}>
          <div className="absolute" style={{ left: pt(72), right: pt(72), top: pt(64), bottom: pt(96) }}>
            <div
              key={index}
              className="h-full flex flex-col justify-center"
              style={{ opacity: visible ? (qrOpen ? 0.2 : 1) : 0, transition: `opacity ${half}ms ease-in-out` }}
            >
              {slide.lines.length === 0 ? (
                <p className="text-white text-center" style={{ fontSize: pt(metrics.bodyPt), lineHeight: 1.25 }} data-testid="present-mode-slide-title">
                  {slide.title}
                </p>
              ) : (
                slide.lines.map((line, i) =>
                  line.text === "" ? (
                    <div key={i} style={{ height: pt(metrics.bodyPt * 0.6) }} />
                  ) : (
                    <p
                      key={i}
                      className="text-white"
                      style={{ fontSize: pt(line.label ? metrics.labelPt : metrics.bodyPt), lineHeight: 1.25, marginBottom: pt(6) }}
                    >
                      {line.text}
                    </p>
                  )
                )
              )}
            </div>
          </div>

          <div
            className="absolute flex items-center justify-between text-white/45"
            style={{ left: pt(48), right: pt(48), bottom: pt(28), fontSize: pt(13), opacity: visible ? 1 : 0, transition: `opacity ${half}ms ease-in-out` }}
          >
            <span className="truncate pr-4 uppercase tracking-widest" data-testid="present-mode-footer-section">{slideFooter(slide)}</span>
            <span className="tracking-widest shrink-0" data-testid="present-mode-page-indicator">{index + 1} / {slides.length}</span>
          </div>

          {songMap.length > 1 && (
            <div
              className="absolute flex items-center justify-center flex-wrap gap-1.5"
              style={{ left: pt(48), right: pt(48), bottom: pt(54), opacity: visible ? 1 : 0, transition: `opacity ${half}ms ease-in-out` }}
              data-testid="present-mode-songmap"
            >
              {songMap.map(({ slideIndex, label }, i) => (
                <button
                  key={i}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={(e) => { e.stopPropagation(); goTo(slideIndex); }}
                  className={`font-mono text-[11px] uppercase tracking-widest rounded-full border transition-colors whitespace-nowrap px-2.5 py-1 focus:outline-none focus-visible:ring-0 ${
                    slideIndex === activeSongMapSlide
                      ? "border-white/70 text-white bg-white/10"
                      : "border-white/15 text-white/40 hover:text-white/70 hover:border-white/40"
                  }`}
                  title={`Jump to ${label}`}
                  data-testid={`present-mode-songmap-pill-${i}`}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div
        className={`absolute inset-0 bg-black transition-opacity duration-500 ${blanked ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        onClick={() => setBlanked(false)}
        data-testid="present-mode-blank-overlay"
      />

      <FollowQrHotspot url={followUrl} followers={followers} onVisibilityChange={setQrOpen} />

      <div className="absolute bottom-4 right-4 flex items-center gap-3 opacity-20 hover:opacity-100 transition-opacity">
        <button onClick={(e) => { e.stopPropagation(); prev(); }} disabled={index === 0} className="p-2 rounded-full hover:bg-white/10 disabled:opacity-20" data-testid="present-mode-prev-button">
          <ChevronLeft className="h-5 w-5" />
        </button>
        <button onClick={(e) => { e.stopPropagation(); next(); }} disabled={index === slides.length - 1} className="p-2 rounded-full hover:bg-white/10 disabled:opacity-20" data-testid="present-mode-next-button">
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}
